import { useProctoredAttempt } from '../components/focus-guard/FocusGuardContext';
import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { MainLayout } from '../components/layout/MainLayout';
import { AICore } from '../components/ai/AICore';
import { ScoreDisplay } from '../components/ai/ScoreDisplay';
import { VoiceWaveform } from '../components/ai/VoiceWaveform';
import { TTSPlayer } from '../components/ai/TTSPlayer';
import { SessionHistory } from '../components/ai/SessionHistory';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Progress } from '../components/ui/progress';
import { ScrollArea } from '../components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
    Play, 
    Pause, 
    Square, 
    Mic, 
    MicOff, 
    Send, 
    Volume2,
    VolumeX,
    RefreshCw,
    ChevronRight,
    History,
    MessageSquare,
    Sparkles,
    Clock,
    GraduationCap,
    BookOpen
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const Training = () => {
    const { api, user } = useAuth();
    const navigate = useNavigate();
    const [modules, setModules] = useState([]);
    const [topics, setTopics] = useState([]);
    const [selectedModule, setSelectedModule] = useState('');
    const [selectedTopic, setSelectedTopic] = useState('');
    const [session, setSession] = useState(null);
    useProctoredAttempt({ active: session?.status === 'active', label: 'training session', onForceEnd: () => completeTraining() });
    const [messages, setMessages] = useState([]);
    const [inputMessage, setInputMessage] = useState('');
    const [aiState, setAiState] = useState('idle');
    const [isRecording, setIsRecording] = useState(false);
    const [loading, setLoading] = useState(false);
    const [scores, setScores] = useState(null);
    const [audioLevel, setAudioLevel] = useState(0);
    const [autoTTS, setAutoTTS] = useState(true);
    const [currentPlayingIndex, setCurrentPlayingIndex] = useState(null);
    const messagesEndRef = useRef(null);
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);
    const audioContextRef = useRef(null);
    const analyserRef = useRef(null);
    const sessionRef = useRef(null);

    // Keep sessionRef updated
    useEffect(() => {
        sessionRef.current = session;
    }, [session]);

    // Memoized scrollToBottom
    const scrollToBottom = useCallback(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, []);

    // Memoized fetch function
    const fetchModulesAndTopics = useCallback(async () => {
        try {
            const [modulesRes, topicsRes] = await Promise.all([
                api.get('/modules'),
                api.get('/topics')
            ]);
            setModules(modulesRes.data);
            setTopics(topicsRes.data.filter(t => t.status === 'approved'));
        } catch (error) {
            console.error('Failed to fetch data:', error);
            toast.error('Failed to load training content');
        }
    }, [api]);

    useEffect(() => {
        fetchModulesAndTopics();
    }, [fetchModulesAndTopics]);

    useEffect(() => {
        scrollToBottom();
    }, [messages, scrollToBottom]);

    const startTraining = async () => {
        if (!selectedModule || !selectedTopic) {
            toast.error('Please select a module and topic');
            return;
        }

        setLoading(true);
        try {
            const response = await api.post('/training/start', {
                module_id: selectedModule,
                topic_id: selectedTopic
            });
            setSession(response.data);
            setMessages(response.data.messages);
            setAiState('speaking');
            setTimeout(() => setAiState('idle'), 2000);
            toast.success('Training session started!');
        } catch (error) {
            console.error('Failed to start training:', error);
            toast.error('Failed to start training session');
        } finally {
            setLoading(false);
        }
    };

    const sendMessage = async () => {
        if (!inputMessage.trim() || !session) return;

        const userMessage = { role: 'user', content: inputMessage, timestamp: new Date().toISOString() };
        setMessages(prev => [...prev, userMessage]);
        setInputMessage('');
        setAiState('thinking');

        try {
            const response = await api.post(`/training/${session.id}/message`, {
                message: inputMessage
            });
            
            const assistantMessage = { 
                role: 'assistant', 
                content: response.data.response, 
                timestamp: new Date().toISOString() 
            };
            setMessages(prev => [...prev, assistantMessage]);
            setScores(response.data.scores);
            setSession(prev => ({ ...prev, progress: response.data.progress }));
            setAiState('speaking');
            
            if (autoTTS) {
                setCurrentPlayingIndex(messages.length + 1);
            }
            
            setTimeout(() => setAiState('idle'), 2000);
        } catch (error) {
            console.error('Failed to send message:', error);
            toast.error('Failed to send message');
            setAiState('idle');
        }
    };

    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            
            // Set up audio analysis for waveform
            audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
            analyserRef.current = audioContextRef.current.createAnalyser();
            const source = audioContextRef.current.createMediaStreamSource(stream);
            source.connect(analyserRef.current);
            analyserRef.current.fftSize = 256;
            
            // Start level monitoring
            const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
            const monitorLevel = () => {
                if (analyserRef.current) {
                    analyserRef.current.getByteFrequencyData(dataArray);
                    const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
                    setAudioLevel(average / 255);
                }
                if (isRecording) {
                    requestAnimationFrame(monitorLevel);
                }
            };
            
            const mediaRecorder = new MediaRecorder(stream);
            mediaRecorderRef.current = mediaRecorder;
            audioChunksRef.current = [];

            mediaRecorder.ondataavailable = (event) => {
                audioChunksRef.current.push(event.data);
            };

            mediaRecorder.onstop = async () => {
                const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
                stream.getTracks().forEach(track => track.stop());
                if (audioContextRef.current) {
                    audioContextRef.current.close();
                }
                // Use ref to avoid stale closure
                await sendVoiceMessage(audioBlob, sessionRef.current);
            };

            mediaRecorder.start();
            setIsRecording(true);
            setAiState('listening');
            monitorLevel();
            toast.info('Recording started - speak clearly');
        } catch (error) {
            console.error('Failed to start recording:', error);
            toast.error('Could not access microphone');
        }
    };

    const stopRecording = () => {
        if (mediaRecorderRef.current && isRecording) {
            mediaRecorderRef.current.stop();
            setIsRecording(false);
            setAudioLevel(0);
        }
    };

    const sendVoiceMessage = async (audioBlob, currentSession) => {
        // Use passed parameter to avoid stale closure
        const sess = currentSession || sessionRef.current;
        
        if (!sess) {
            toast.error('Session expired. Please start a new training session.');
            return;
        }

        setAiState('thinking');
        const formData = new FormData();
        formData.append('audio', audioBlob, 'recording.webm');

        try {
            const response = await api.post(`/training/${sess.id}/voice`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            const userMessage = { 
                role: 'user', 
                content: response.data.transcript, 
                type: 'voice',
                timestamp: new Date().toISOString()
            };
            const assistantMessage = { 
                role: 'assistant', 
                content: response.data.response,
                timestamp: new Date().toISOString()
            };
            
            setMessages(prev => [...prev, userMessage, assistantMessage]);
            setScores({
                ...response.data.communication_scores,
                voice: response.data.voice_scores
            });
            setSession(prev => ({ ...prev, progress: response.data.progress }));
            setAiState('speaking');
            
            if (autoTTS) {
                setCurrentPlayingIndex(messages.length + 1);
            }
            
            setTimeout(() => setAiState('idle'), 2000);
        } catch (error) {
            console.error('Failed to process voice:', error);
            toast.error('Failed to process voice input');
            setAiState('idle');
        }
    };

    const pauseTraining = async () => {
        if (!session) return;
        try {
            await api.post(`/training/${session.id}/pause`);
            setSession(prev => ({ ...prev, status: 'paused' }));
            toast.info('Training paused');
        } catch (error) {
            toast.error('Failed to pause training');
        }
    };

    const resumeTraining = async () => {
        if (!session) return;
        try {
            await api.post(`/training/${session.id}/resume`);
            setSession(prev => ({ ...prev, status: 'active' }));
            toast.success('Training resumed');
        } catch (error) {
            toast.error('Failed to resume training');
        }
    };

    const completeTraining = async () => {
        if (!session) return;
        try {
            const response = await api.post(`/training/${session.id}/complete`);
            toast.success('Training completed!', {
                description: `Final score: ${response.data.final_scores.communication_score}`
            });
            setSession(null);
            setMessages([]);
            setScores(null);
        } catch (error) {
            toast.error('Failed to complete training');
        }
    };

    const handleResumeSession = (existingSession) => {
        setSession(existingSession);
        setMessages(existingSession.messages || []);
        setScores(null);
        toast.success('Session resumed!');
    };

    const getTopicName = (topicId) => {
        const topic = topics.find(t => t.id === topicId);
        return topic?.name || 'Training';
    };

    return (
        <MainLayout>
            <div className="space-y-6 animate-fade-in" data-testid="training-page">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-3xl font-heading font-bold tracking-tight flex items-center gap-3">
                            <Sparkles className="h-8 w-8 text-primary" />
                            AI Training Session
                        </h1>
                        <p className="text-muted-foreground mt-1">Interactive learning with your AI recruitment trainer</p>
                    </div>
                    {session && (
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setAutoTTS(!autoTTS)}
                            className="gap-2"
                        >
                            {autoTTS ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
                            Auto-speak: {autoTTS ? 'On' : 'Off'}
                        </Button>
                    )}
                </div>

                {!session ? (
                    /* Session Setup */
                    <Tabs defaultValue="new" className="w-full">
                        <TabsList className="grid w-full grid-cols-2 max-w-md">
                            <TabsTrigger value="new" className="gap-2">
                                <Play className="h-4 w-4" />
                                New Session
                            </TabsTrigger>
                            <TabsTrigger value="history" className="gap-2">
                                <History className="h-4 w-4" />
                                History
                            </TabsTrigger>
                        </TabsList>
                        
                        <TabsContent value="new" className="mt-6">
                            <Card className="max-w-2xl">
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <GraduationCap className="h-5 w-5" />
                                        Start New Training
                                    </CardTitle>
                                    <CardDescription>
                                        Select a module and topic to begin your personalized AI training session
                                    </CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <div className="space-y-2">
                                        <label className="text-sm font-medium">Select Module</label>
                                        <Select value={selectedModule} onValueChange={setSelectedModule}>
                                            <SelectTrigger data-testid="select-module">
                                                <SelectValue placeholder="Choose a module" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {modules.map(m => (
                                                    <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>

                                    <div className="space-y-2">
                                        <label className="text-sm font-medium">Select Topic</label>
                                        <Select value={selectedTopic} onValueChange={setSelectedTopic}>
                                            <SelectTrigger data-testid="select-topic">
                                                <SelectValue placeholder="Choose a topic" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {topics.map(t => (
                                                    <SelectItem key={t.id} value={t.id}>
                                                        <div className="flex items-center gap-2">
                                                            {t.name}
                                                            <Badge variant="outline" className="text-xs">
                                                                {t.difficulty_level}
                                                            </Badge>
                                                        </div>
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>

                                    <Button 
                                        onClick={startTraining} 
                                        disabled={loading || !selectedModule || !selectedTopic}
                                        className="w-full btn-primary"
                                        data-testid="start-training"
                                    >
                                        {loading ? (
                                            <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                                        ) : (
                                            <Play className="h-4 w-4 mr-2" />
                                        )}
                                        Start Conversation Training
                                    </Button>

                                    {selectedTopic && (
                                        <Button 
                                            onClick={() => navigate(`/learn/${selectedTopic}`)} 
                                            variant="outline"
                                            className="w-full gap-2"
                                            data-testid="start-interactive-learning"
                                        >
                                            <BookOpen className="h-4 w-4" />
                                            Start Interactive Learning (with Slides)
                                        </Button>
                                    )}
                                </CardContent>
                            </Card>
                        </TabsContent>
                        
                        <TabsContent value="history" className="mt-6">
                            <div className="max-w-2xl">
                                <SessionHistory 
                                    onResumeSession={handleResumeSession}
                                    onViewSession={(s) => {
                                        setSession(s);
                                        setMessages(s.messages || []);
                                    }}
                                />
                            </div>
                        </TabsContent>
                    </Tabs>
                ) : (
                    /* Active Training Session */
                    <div className="grid gap-6 lg:grid-cols-3">
                        {/* Main Training Area */}
                        <div className="lg:col-span-2 space-y-4">
                            {/* AI Avatar & Progress */}
                            <Card className="overflow-hidden">
                                <div className="bg-gradient-to-r from-primary/10 via-primary/5 to-transparent p-6">
                                    <div className="flex items-center justify-between mb-6">
                                        <div>
                                            <h3 className="font-semibold text-lg">{getTopicName(session.topic_id)}</h3>
                                            <div className="flex items-center gap-2 mt-1">
                                                <Clock className="h-4 w-4 text-muted-foreground" />
                                                <span className="text-sm text-muted-foreground">
                                                    Progress: {Math.round(session.progress)}%
                                                </span>
                                                <Badge variant={session.status === 'active' ? 'default' : 'secondary'}>
                                                    {session.status}
                                                </Badge>
                                            </div>
                                        </div>
                                        <div className="flex gap-2">
                                            {session.status === 'active' ? (
                                                <Button variant="outline" size="sm" onClick={pauseTraining} data-testid="pause-training">
                                                    <Pause className="h-4 w-4" />
                                                </Button>
                                            ) : (
                                                <Button variant="outline" size="sm" onClick={resumeTraining} data-testid="resume-training">
                                                    <Play className="h-4 w-4" />
                                                </Button>
                                            )}
                                            <Button variant="destructive" size="sm" onClick={completeTraining} data-testid="complete-training">
                                                <Square className="h-4 w-4 mr-1" />
                                                End
                                            </Button>
                                        </div>
                                    </div>
                                    <Progress value={session.progress} className="h-2 mb-6" />
                                    
                                    <div className="flex justify-center py-4">
                                        <AICore state={aiState} size="lg" />
                                    </div>
                                </div>
                            </Card>

                            {/* Chat Area */}
                            <Card>
                                <CardContent className="p-0">
                                    <ScrollArea className="h-[350px] p-4">
                                        <div className="space-y-4">
                                            {messages.map((msg, idx) => (
                                                <div
                                                    key={`msg-${msg.timestamp || idx}-${msg.role}`}
                                                    className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                                                >
                                                    <div
                                                        className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                                                            msg.role === 'user'
                                                                ? 'bg-primary text-primary-foreground'
                                                                : 'bg-muted'
                                                        }`}
                                                    >
                                                        {msg.type === 'voice' && (
                                                            <span className="text-xs opacity-70 flex items-center gap-1 mb-1">
                                                                <Mic className="h-3 w-3" /> Voice message
                                                            </span>
                                                        )}
                                                        <p className="text-sm leading-relaxed">{msg.content}</p>
                                                        
                                                        {/* TTS Button for AI messages */}
                                                        {msg.role === 'assistant' && (
                                                            <div className="flex items-center justify-between mt-2 pt-2 border-t border-white/10">
                                                                <span className="text-xs opacity-50">
                                                                    {msg.timestamp && new Date(msg.timestamp).toLocaleTimeString()}
                                                                </span>
                                                                <TTSPlayer 
                                                                    text={msg.content}
                                                                    api={api}
                                                                    autoPlay={autoTTS && currentPlayingIndex === idx}
                                                                    onPlay={() => setAiState('speaking')}
                                                                    onEnd={() => {
                                                                        setAiState('idle');
                                                                        setCurrentPlayingIndex(null);
                                                                    }}
                                                                />
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                            <div ref={messagesEndRef} />
                                        </div>
                                    </ScrollArea>

                                    {/* Voice Waveform */}
                                    {isRecording && (
                                        <div className="px-4 pb-2">
                                            <VoiceWaveform isRecording={isRecording} audioLevel={audioLevel} />
                                        </div>
                                    )}

                                    {/* Input Area */}
                                    <div className="border-t p-4">
                                        <div className="flex gap-2">
                                            <Button
                                                variant={isRecording ? 'destructive' : 'outline'}
                                                size="icon"
                                                onClick={isRecording ? stopRecording : startRecording}
                                                disabled={session.status !== 'active'}
                                                data-testid="voice-btn"
                                                className="shrink-0"
                                            >
                                                {isRecording ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
                                            </Button>
                                            <Input
                                                value={inputMessage}
                                                onChange={(e) => setInputMessage(e.target.value)}
                                                onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                                                placeholder="Type your response or use voice..."
                                                disabled={session.status !== 'active' || isRecording}
                                                className="flex-1"
                                                data-testid="message-input"
                                            />
                                            <Button
                                                onClick={sendMessage}
                                                disabled={!inputMessage.trim() || session.status !== 'active'}
                                                data-testid="send-btn"
                                                className="shrink-0"
                                            >
                                                <Send className="h-4 w-4" />
                                            </Button>
                                        </div>
                                        <p className="text-xs text-muted-foreground mt-2 text-center">
                                            Press <kbd className="px-1.5 py-0.5 bg-muted rounded text-xs">Enter</kbd> to send or click the microphone to speak
                                        </p>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>

                        {/* Scores Sidebar */}
                        <div className="space-y-4">
                            <Card>
                                <CardHeader className="pb-3">
                                    <CardTitle className="text-lg flex items-center gap-2">
                                        <MessageSquare className="h-5 w-5 text-primary" />
                                        Communication Score
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    {scores ? (
                                        <ScoreDisplay scores={scores} type="communication" />
                                    ) : (
                                        <div className="flex flex-col items-center justify-center py-8 text-center">
                                            <MessageSquare className="h-8 w-8 text-muted-foreground mb-2" />
                                            <p className="text-sm text-muted-foreground">
                                                Start chatting to see your scores
                                            </p>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>

                            {scores?.voice && (
                                <Card>
                                    <CardHeader className="pb-3">
                                        <CardTitle className="text-lg flex items-center gap-2">
                                            <Mic className="h-5 w-5 text-secondary" />
                                            Voice Score
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <ScoreDisplay scores={scores.voice} type="voice" />
                                    </CardContent>
                                </Card>
                            )}

                            {/* Tips Card */}
                            <Card className="bg-primary/5 border-primary/20">
                                <CardHeader className="pb-2">
                                    <CardTitle className="text-sm font-medium">Training Tips</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <ul className="text-xs text-muted-foreground space-y-2">
                                        <li className="flex gap-2">
                                            <span className="text-primary">•</span>
                                            Speak clearly and confidently
                                        </li>
                                        <li className="flex gap-2">
                                            <span className="text-primary">•</span>
                                            Explain concepts in your own words
                                        </li>
                                        <li className="flex gap-2">
                                            <span className="text-primary">•</span>
                                            Ask questions if unclear
                                        </li>
                                        <li className="flex gap-2">
                                            <span className="text-primary">•</span>
                                            Practice with voice for better scores
                                        </li>
                                    </ul>
                                </CardContent>
                            </Card>
                        </div>
                    </div>
                )}
            </div>
        </MainLayout>
    );
};

export default Training;
